import { Button, Card, Form, Input, Popconfirm, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Loan = {
  id: string;
  accession_no?: string;
  title_name?: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  student_id: string;
  issued_on: string;
  due_on: string;
  returned_on?: string | null;
  status: string;
  fine_amount?: number | null;
  fine_demand_id?: string | null;
};

export default function LibraryLoansPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const studentFilter = searchParams.get('student_id') || undefined;
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canCirculate = Boolean(
    user?.permissions?.includes('education.library.circulate') ||
      user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>('issued');
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-library-loans', studentFilter, status, q],
    queryFn: async () => {
      const res = await api.get<Loan[]>('/education/library/loans', {
        params: { student_id: studentFilter, status, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const returnLoan = useMutation({
    mutationFn: (id: string) => api.post(`/education/library/loans/${id}/return`, { raise_fine: true }),
    onSuccess: (result) => {
      const body = result.data as { fine_amount?: number; late_days?: number };
      if (body?.fine_amount && body.fine_amount > 0) {
        message.success(t('libraryReturnedWithFine', { amount: body.fine_amount, days: body.late_days ?? 0 }));
      } else {
        message.success(t('libraryReturned'));
      }
      queryClient.invalidateQueries({ queryKey: ['education-library-loans'] });
      queryClient.invalidateQueries({ queryKey: ['education-library-titles'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const renewLoan = useMutation({
    mutationFn: (id: string) => api.post(`/education/library/loans/${id}/renew`, { extend_days: 14 }),
    onSuccess: (result) => {
      const body = result.data as { due_on?: string };
      message.success(t('libraryRenewed', { due: body?.due_on || '' }));
      queryClient.invalidateQueries({ queryKey: ['education-library-loans'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('libraryLoans')}
      description={t('libraryLoansIntro')}
      extra={
        <Space wrap>
          {canCirculate ? (
            <Button
              type="primary"
              onClick={() =>
                navigate(
                  studentFilter
                    ? `/education/library/issue?student_id=${studentFilter}`
                    : '/education/library/issue'
                )
              }
            >
              {t('libraryIssue')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/library')}>{t('backToLibrary')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 140 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'issued', label: t('issued') },
                  { value: 'overdue', label: t('overdue') },
                  { value: 'returned', label: t('returned') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 200 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
            {studentFilter ? (
              <Button
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('student_id');
                  setSearchParams(next);
                }}
              >
                {t('clearStudentFilter')}
              </Button>
            ) : null}
          </Form>
        </Card>

        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            columns={[
              { title: t('accessionNo'), dataIndex: 'accession_no', width: 120 },
              { title: t('title'), dataIndex: 'title_name' },
              {
                title: t('student'),
                render: (_: unknown, row: Loan) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
              },
              { title: t('issuedOn'), dataIndex: 'issued_on', width: 110 },
              { title: t('dueOn'), dataIndex: 'due_on', width: 110 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 100,
                render: (v: string) => (
                  <Tag color={v === 'issued' ? 'processing' : v === 'overdue' ? 'warning' : 'success'}>{v}</Tag>
                ),
              },
              {
                title: t('fine'),
                dataIndex: 'fine_amount',
                width: 90,
                render: (v) => (v != null ? String(Number(v)) : '—'),
              },
              ...(canCirculate
                ? [
                    {
                      title: t('actions'),
                      width: 200,
                      render: (_: unknown, row: Loan) =>
                        row.status === 'issued' || row.status === 'overdue' ? (
                          <Space size="small" wrap>
                            <Popconfirm
                              title={t('libraryRenewConfirm')}
                              onConfirm={() => renewLoan.mutate(row.id)}
                            >
                              <Button size="small" loading={renewLoan.isPending}>
                                {t('renewItem')}
                              </Button>
                            </Popconfirm>
                            <Popconfirm
                              title={t('libraryReturnConfirm')}
                              onConfirm={() => returnLoan.mutate(row.id)}
                            >
                              <Button size="small" loading={returnLoan.isPending}>
                                {t('returnItem')}
                              </Button>
                            </Popconfirm>
                          </Space>
                        ) : null,
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
